import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Landmark, Languages, Sun, Moon, Monitor, Menu, ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { usePreferences } from "@/lib/preferences";
import { track } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Container, SHOW_EXPLAINER, SmartLink, useCopy } from "./shared";

const THEME_ICONS = { system: Monitor, light: Sun, dark: Moon };
const THEME_LABELS = {
  system: { en: "System", ar: "النظام" },
  light: { en: "Light", ar: "فاتح" },
  dark: { en: "Dark", ar: "داكن" },
};

/** True once the page has scrolled past the very top (drives the header's frosted background). */
function useScrolled(threshold = 12) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

/** Landing anchors in page order (Phase 56: nine blocks). "Model a holding" and "Pricing" are their own public pages. */
export const navLinks = (c) => [
  { id: "personas", href: "#personas", label: c.nav.personas },
  { id: "how", href: "#how", label: c.nav.how },
  { id: "screens", href: "#screens", label: c.nav.screens, menuOnly: true }, // room is tight in the desktop bar; the drawer keeps it
  { id: "calculator", to: "/model", label: c.nav.calculator },
  { id: "pricing", to: "/pricing", label: c.nav.pricing },
  ...(SHOW_EXPLAINER ? [{ id: "explainer", href: "#explainer", label: c.nav.video }] : []),
  { id: "trust", href: "#trust", label: c.nav.trust },
  { id: "faq", href: "#faq", label: c.nav.faq },
];

/** The landing page lives at `/` (EN) and `/ar` (AR); switching the language on it moves between the two addresses. */
export function useLandingLanguageToggle() {
  const { lang, setLang } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return () => {
    const to = lang === "en" ? "ar" : "en";
    track("landing_language", { to });
    if (pathname === "/" || pathname === "/ar") navigate(to === "ar" ? "/ar" : "/", { replace: true });
    setLang(to);
  };
}

function LandingBrand({ tagline }) {
  return (
    <Link to="/" className="flex shrink-0 items-center gap-2 sm:gap-2.5" data-testid="landing-logo">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-[var(--shadow-sm)]"><Landmark size={18} /></span>
      {/* The tagline only shows while the full nav is collapsed into the menu (md–xl); beside the full nav there is no room for it. */}
      <span className="min-w-0 leading-tight"><span className="block whitespace-nowrap text-sm font-semibold tracking-tight sm:text-base">Tamreer · <span className="font-ar">تمرير</span></span><span className="hidden max-w-[22rem] truncate text-2xs text-muted-foreground md:block xl:hidden">{tagline}</span></span>
    </Link>
  );
}

function LandingThemeMenu({ theme, setTheme, lang }) {
  const ThemeIcon = THEME_ICONS[theme] || Monitor;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><button type="button" className="lp-focus inline-flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-card transition-colors hover:bg-accent/60 sm:h-9 sm:w-9" aria-label={lang === "ar" ? "المظهر" : "Appearance"} data-testid="landing-theme-menu"><ThemeIcon size={15} /></button></DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {["system", "light", "dark"].map((k) => { const I = THEME_ICONS[k]; return <DropdownMenuItem key={k} onClick={() => setTheme(k)} data-testid={`landing-theme-${k}`}><I size={14} className="me-2" />{THEME_LABELS[k][lang === "ar" ? "ar" : "en"]}</DropdownMenuItem>; })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Language toggle (44 px on phones — committee R-4), appearance menu, sign-in / create-account (≥ sm) and the menu button. */
function HeaderActions({ c, lang, onToggleLang, theme, setTheme, onOpenMenu, showMenu }) {
  return (
    <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
      <button type="button" onClick={onToggleLang} data-testid="landing-language-toggle" aria-label={lang === "en" ? "التبديل إلى العربية" : "Switch to English"}
        className="lp-focus inline-flex h-11 min-w-[2.75rem] items-center justify-center gap-1.5 rounded-lg border border-border bg-card px-2.5 text-xs font-medium transition-colors hover:bg-accent/60 sm:h-9 sm:min-w-0 sm:text-sm">
        <Languages size={15} /><span className="hidden min-[400px]:inline">{lang === "en" ? "العربية" : "English"}</span>
      </button>
      <LandingThemeMenu theme={theme} setTheme={setTheme} lang={lang} />
      <Button asChild variant="ghost" className="hidden sm:inline-flex" data-testid="landing-header-sign-in-button"><Link to="/auth">{c.header.signIn}</Link></Button>
      <Button asChild className="hidden sm:inline-flex shadow-[var(--shadow-sm)]" data-testid="landing-header-create-account-button"><Link to="/auth?mode=register" onClick={() => track("landing_cta_click", { placement: "header" })}>{c.header.create}</Link></Button>
      {showMenu && <Button variant="outline" size="icon" className="h-11 w-11 sm:h-9 sm:w-9 xl:hidden" onClick={onOpenMenu} aria-label={lang === "ar" ? "القائمة" : "Menu"} data-testid="landing-menu-button"><Menu size={18} /></Button>}
    </div>
  );
}

function LandingMobileMenu({ open, onOpenChange, links, c, lang }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={lang === "ar" ? "right" : "left"} className="w-[300px]" data-testid="landing-mobile-menu">
        <SheetTitle>Tamreer · تمرير</SheetTitle>
        <SheetDescription>{c.header.tagline}</SheetDescription>
        <nav className="mt-6 flex flex-col gap-1" aria-label={lang === "ar" ? "الأقسام" : "Sections"}>
          {links.map((l) => <SmartLink key={l.id} to={l.to} href={l.href} onClick={() => onOpenChange(false)} className="flex min-h-11 items-center rounded-lg px-3 text-sm hover:bg-accent/60" data-testid={`landing-mobile-nav-${l.id}`}>{l.label}</SmartLink>)}
        </nav>
        <div className="mt-6 grid gap-2">
          <Button asChild data-testid="landing-mobile-create-account-button"><Link to="/auth?mode=register" onClick={() => track("landing_cta_click", { placement: "menu" })}>{c.header.create}</Link></Button>
          <Button asChild variant="outline" data-testid="landing-mobile-sign-in-button"><Link to="/auth">{c.header.signIn}</Link></Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Public header. `variant="landing"` carries the section anchors (desktop nav + phone drawer); `variant="page"` (e.g. /model)
 * shows a "back to the overview" link instead, since the anchors would point nowhere.
 */
export function LandingHeader({ variant = "landing" }) {
  const { c, lang, ar } = useCopy();
  const { theme, setTheme } = usePreferences();
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);
  const toggleLang = useLandingLanguageToggle();
  const links = navLinks(c);
  const onLanding = variant === "landing";
  const Back = ar ? ArrowRight : ArrowLeft;
  return (
    <header className={cn("sticky top-0 z-40 transition-colors duration-200", scrolled ? "border-b border-border bg-card/75 backdrop-blur-md" : "bg-transparent")} data-testid="landing-header">
      <Container className="flex h-16 items-center justify-between gap-2 px-3 sm:gap-4 sm:px-6">
        <LandingBrand tagline={c.header.tagline} />
        {onLanding ? (
          <nav className="hidden items-center gap-0.5 xl:flex" data-testid="landing-header-nav" aria-label={ar ? "الأقسام" : "Sections"}>
            {links.filter((l) => !l.menuOnly).map((l) => <SmartLink key={l.id} to={l.to} href={l.href} className="lp-navlink whitespace-nowrap rounded-md px-2 py-1.5 text-[0.8rem] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`landing-nav-${l.id}`}>{l.label}</SmartLink>)}
          </nav>
        ) : (
          <Link to={ar ? "/ar" : "/"} className="lp-focus hidden items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground sm:inline-flex" data-testid="landing-back-home-link"><Back size={14} />{c.calculator.back}</Link>
        )}
        <HeaderActions c={c} lang={lang} onToggleLang={toggleLang} theme={theme} setTheme={setTheme} onOpenMenu={() => setOpen(true)} showMenu={onLanding} />
      </Container>
      {onLanding && <LandingMobileMenu open={open} onOpenChange={setOpen} links={links} c={c} lang={lang} />}
    </header>
  );
}
